import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Search, Plus, Bell, User, MessageSquare, Sparkles, Calendar, X, Camera } from 'lucide-react';
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { onChatRead } from '../lib/unreadBus';

const tabs = [
  { path: '/', icon: Home, label: 'Home' },
  { path: '/explore', icon: Search, label: 'Explore' },
  { isCreate: true, label: 'Create' },
  { path: '/activity', icon: Bell, label: 'Activity', hasBadge: true },
  { path: '/profile', icon: User, label: 'Profile' },
];

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showSheet, setShowSheet] = useState(false);

  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const { data } = await api.get('/activity/unread-count');
        setUnreadCount(data.count);
      } catch {}
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    // Refresh immediately whenever any chat is marked read so the badge
    // doesn't stay stale for 30 seconds. Also refresh when the tab
    // returns to the foreground.
    const offBus = onChatRead(() => fetchUnread());
    const onVis = () => { if (!document.hidden) fetchUnread(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(interval);
      offBus();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const createOptions = [
    {
      key: 'story',
      icon: Camera,
      label: 'New Story',
      desc: 'Snap or upload — disappears in 24h',
      onClick: () => { setShowSheet(false); navigate('/story/camera'); },
    },
    {
      key: 'vibe',
      icon: Sparkles,
      label: 'New Vibe',
      desc: 'Share a thought, photo, or moment',
      onClick: () => { setShowSheet(false); navigate('/profile', { state: { openVibe: true } }); },
    },
    {
      key: 'group-chat',
      icon: MessageSquare,
      label: 'New Group Chat',
      desc: 'Start a private chat with friends',
      onClick: () => { setShowSheet(false); navigate('/new-group-chat'); },
    },
    {
      key: 'plan',
      icon: Calendar,
      label: 'New Plan',
      desc: 'Host an event and invite people',
      onClick: () => { setShowSheet(false); navigate('/create'); },
    },
  ];

  return (
    <>
      <nav className="bottom-nav" data-testid="bottom-nav">
        <div className="flex items-center justify-around px-2 pt-2 pb-1">
          {tabs.map((tab, idx) => {
            if (tab.isCreate) {
              return (
                <button
                  key="create"
                  data-testid="nav-create-btn"
                  onClick={() => setShowSheet(true)}
                  className="relative -mt-5 flex items-center justify-center w-14 h-14 rounded-full bg-[#B6E600] text-[#111111] shadow-[0_4px_20px_rgba(212,255,0,0.25)] active:scale-90 transition-transform"
                >
                  <Plus size={26} strokeWidth={2.5} />
                </button>
              );
            }

            const isActive = location.pathname === tab.path;
            const Icon = tab.icon;

            return (
              <button
                key={tab.path}
                data-testid={`nav-${tab.label.toLowerCase()}-btn`}
                onClick={() => navigate(tab.path)}
                className={`relative flex flex-col items-center gap-0.5 py-1 px-3 transition-colors ${
                  isActive ? 'text-[#111111]' : 'text-[#9B9EA3]'
                }`}
              >
                <Icon size={22} strokeWidth={isActive ? 2.5 : 1.5} />
                <span className="text-[10px] font-medium">{tab.label}</span>
                {tab.hasBadge && unreadCount > 0 && (
                  <span
                    data-testid="activity-badge"
                    className="absolute -top-0.5 right-1 w-2.5 h-2.5 bg-[#FF3B30] rounded-full shadow-[0_0_8px_rgba(255,59,48,0.6)]"
                  />
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {showSheet && (
        <div
          className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/30 animate-fade-in"
          onClick={() => setShowSheet(false)}
          data-testid="create-sheet-overlay"
        >
          <div
            className="w-full max-w-[480px] bg-white rounded-t-3xl animate-slide-up pb-safe-area"
            onClick={e => e.stopPropagation()}
            data-testid="create-sheet"
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-[#E5E4DE] rounded-full" />
            </div>
            <div className="flex items-center justify-between px-5 py-3">
              <h3 className="text-lg font-bold text-[#111111]">Create</h3>
              <button
                onClick={() => setShowSheet(false)}
                className="p-1 text-[#9B9EA3]"
                data-testid="create-sheet-close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="px-3">
              {createOptions.map(opt => {
                const OptIcon = opt.icon;
                return (
                  <button
                    key={opt.key}
                    data-testid={`create-${opt.key}-btn`}
                    onClick={opt.onClick}
                    className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
                  >
                    <div className="w-11 h-11 rounded-2xl bg-[#B6E600]/15 flex items-center justify-center flex-shrink-0">
                      <OptIcon size={20} className="text-[#3F5200]" strokeWidth={2} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold text-[#111111]">{opt.label}</p>
                      <p className="text-xs text-[#9B9EA3] mt-0.5">{opt.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
